import React, { useEffect } from "react";
import { useFormik } from "formik";
import { useNavigate, useSearchParams } from "react-router-dom";
import * as yup from "yup";

import {
  Box,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import Button from "@mui/material/Button";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

import { useSnackbar } from "contexts";
import { signIn, signOut, getUserClients } from "services";
import { Session, getClientPrefix } from "utils";
import { useNavigateWithClient } from "hooks";

import { fieldSx, FieldLabel, ErrorText } from "../../components/FormFields";

/**
 * MobileLogin — the mobile port of the desktop `screens/Login.jsx`.
 *
 * Same login logic as desktop (identical two-step flow: enter email → Next
 * loads the user's org(s) → password [+ org select when >1 org] → Login), the
 * same platform calls (signIn/signOut/getUserClients), and the same validation.
 * ONLY the layout differs: the desktop side-by-side LoginShell is stacked into a
 * gradient hero (org logo + tagline) above a white form panel.
 *
 * Rendered by `mobile/index.jsx` at `/:client/login` — where `PrivateRoute`
 * redirects unauthenticated users. Standalone page, no MobileAppShell (the user
 * is logged out here), mirroring desktop which shows no nav on login.
 */

const LoginValidationSchema = yup.object({
  isOrganization: yup.boolean(),
  email: yup
    .string()
    .email("Please enter a correct email")
    .required("Please provide a registered email"),
  password: yup
    .string()
    .min(8, "Minimum 8 characters required for the password")
    .required("Please provide a password"),
  client: yup.string().required("Please select a Organization"),
});

function MobileLogin() {
  const snackbar = useSnackbar();
  const navigate = useNavigate();
  const navigateWithClient = useNavigateWithClient();
  const [searchParams] = useSearchParams();

  const [clients, setClients] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [logo, setLogo] = React.useState(null);
  const [showPassword, setShowPassword] = React.useState(false);

  const [isB2cLogin, setIsB2cLogin] = React.useState(false);

  React.useEffect(() => {
    setIsB2cLogin(searchParams.get("b2c"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formik = useFormik({
    initialValues: { email: "", password: "", client: "" },
    validationSchema: LoginValidationSchema,
    onSubmit: async (values) => {
      try {
        setLoading(true);

        const { user } = await signIn({
          email: values.email,
          password: values.password,
          client: isB2cLogin
            ? process.env.REACT_APP_LANGUIFY_ADMIN_USER_ID
            : values.client,
        });

        const deactivated = Session.checkDeactivatedStatus();

        if (deactivated) {
          await signOut();
          navigate("/error-admin");
        } else {
          navigate(`/${getClientPrefix(user)}/home`);
        }
      } catch (error) {
        console.error(error);
        snackbar.error(error.message);
      } finally {
        setLoading(false);
      }
    },
  });

  const handleNext = async () => {
    try {
      setLoading(true);
      const clientList = await getUserClients(formik.values.email);

      clientList.forEach((c) => (c.label = c.name));

      if (clientList.length) {
        setClients(clientList);
        formik.setFieldValue("client", clientList[0]._id);
      } else formik.setFieldError("email", "Please provide a registered email");
    } catch (error) {
      formik.setFieldError("email", "Please provide a registered email");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (Boolean(sessionStorage.getItem("password_reset"))) {
      sessionStorage.removeItem("password_reset");
    }
  }, []);

  useEffect(() => {
    if (Session.isLoggedIn()) navigateWithClient("/home");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const client = clients.find((c) => c._id === formik.values.client);
    setLogo({
      url: client?.metadata?.logoUrl,
      transform: client?.metadata?.inprepLogoTransform,
    });
  }, [formik.values.client, clients]);

  const hasClients = clients.length !== 0;
  const showOrgSelect = clients.length > 1 && !isB2cLogin;

  const emailError =
    formik.touched.email && !!formik.errors.email ? formik.errors.email : "";
  const clientError =
    formik.touched.client && !!formik.errors.client ? formik.errors.client : "";
  const passwordError =
    formik.touched.password && !!formik.errors.password
      ? formik.errors.password
      : "";

  return (
    <Box
      sx={{
        minHeight: "100dvh",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "shades.clr-white-900",
      }}
    >
      {/* Gradient hero — desktop LoginShell left panel, minus the demo GIF. */}
      <Box
        sx={{
          background: "linear-gradient(278.27deg, #4B6CB7 0%, #002759 112.69%)",
          px: 6,
          py: 10,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 6,
        }}
      >
        {logo?.url && (
          <Box
            sx={{
              width: 200,
              height: 68,
              border: "2px solid #AFAFAF",
              borderRadius: 1,
              backgroundColor: "shades.clr-white-900",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              overflow: "hidden",
            }}
          >
            <img
              src={logo.url}
              alt="Inprep"
              style={{
                objectFit: "cover",
                height: 64,
                transform: `translate(${logo?.transform?.translate?.x || 0}px, ${
                  logo?.transform?.translate?.y || 0
                }px) rotate(${logo?.transform?.rotation || 0}deg) scale(${
                  logo?.transform?.scale || 1
                })`,
              }}
            />
          </Box>
        )}

        <Typography
          variant="h3-medium"
          color="shades.clr-white-900"
          sx={{ textAlign: "center" }}
        >
          Get Into Your Personalized Preparation Zone
        </Typography>
      </Box>

      {/* Form panel — desktop LoginShell right panel. */}
      <Box
        sx={{
          flex: 1,
          px: 6,
          pt: 8,
          pb: 10,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Typography variant="h2-medium" color="shades.clr-black-900">
          Login
        </Typography>

        <Box
          component="form"
          onSubmit={formik.handleSubmit}
          noValidate
          sx={{
            width: "100%",
            mt: 8,
            display: "flex",
            flexDirection: "column",
            gap: 5,
          }}
        >
          {/* Email */}
          <Box>
            <FieldLabel>Enter your registered email</FieldLabel>
            <TextField
              id="email"
              name="email"
              placeholder="example@gmail.com"
              autoFocus
              fullWidth
              size="small"
              spellCheck="false"
              value={formik.values.email}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled={hasClients}
              sx={(theme) => fieldSx(theme, !!emailError)}
              InputProps={
                emailError
                  ? {
                      endAdornment: (
                        <InputAdornment position="end">
                          <WarningAmberOutlinedIcon
                            fontSize="small"
                            sx={{ color: "danger.main" }}
                          />
                        </InputAdornment>
                      ),
                    }
                  : undefined
              }
            />
            {emailError && <ErrorText>{emailError}</ErrorText>}
          </Box>

          {/* Organization select — only when the email spans >1 org. */}
          {showOrgSelect && (
            <Box>
              <FieldLabel>Select Organization</FieldLabel>
              <Select
                name="client"
                fullWidth
                size="small"
                displayEmpty
                value={formik.values.client || ""}
                onChange={formik.handleChange}
                IconComponent={KeyboardArrowDownIcon}
                MenuProps={{ PaperProps: { sx: { maxHeight: 320 } } }}
                sx={(theme) => ({
                  height: "44px",
                  borderRadius: theme.spacing(1),
                  backgroundColor: theme.palette.shades["clr-white-900"],
                  ...theme.typography["body01-medium"],
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: clientError
                      ? theme.palette.danger.main
                      : theme.palette.neutral["clr-500"],
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: theme.palette.primary["clr-300"],
                  },
                  "& .MuiSelect-select": { py: 2 },
                })}
                renderValue={(selected) => {
                  const chosen = clients.find((c) => c._id === selected);
                  if (!chosen) {
                    return (
                      <Typography
                        variant="body01-medium"
                        color="neutral.clr-500"
                      >
                        Select Organization
                      </Typography>
                    );
                  }
                  return (
                    <Typography variant="body01-medium" color="neutral.clr-900">
                      {chosen.name}
                    </Typography>
                  );
                }}
              >
                {clients.map((client) => (
                  <MenuItem key={client._id} value={client._id}>
                    <Typography
                      variant="body01-medium"
                      color="neutral.clr-900"
                      noWrap
                    >
                      {client.name}
                    </Typography>
                  </MenuItem>
                ))}
              </Select>
              {clientError && <ErrorText>{clientError}</ErrorText>}
            </Box>
          )}

          {/* Password — revealed once the org(s) are loaded. */}
          {hasClients && (
            <Box>
              <FieldLabel>Enter password</FieldLabel>
              <TextField
                id="password"
                name="password"
                placeholder="xxxxxxx"
                fullWidth
                size="small"
                spellCheck="false"
                type={showPassword ? "text" : "password"}
                value={formik.values.password}
                onChange={formik.handleChange}
                sx={(theme) => fieldSx(theme, !!passwordError)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword((prev) => !prev)}
                        onMouseDown={(e) => e.preventDefault()}
                        size="small"
                      >
                        {showPassword ? (
                          <VisibilityOff fontSize="small" />
                        ) : (
                          <Visibility fontSize="small" />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              {passwordError && <ErrorText>{passwordError}</ErrorText>}
            </Box>
          )}

          {/* Actions */}
          <Box sx={{ display: "flex", gap: 3, mt: 2 }}>
            {hasClients && (
              <Button
                type="button"
                variant="outlined"
                onClick={() => setClients([])}
                sx={{ flex: 1 }}
              >
                Back
              </Button>
            )}
            <LoadingButton
              type="button"
              variant="contained"
              loading={loading}
              onClick={hasClients ? formik.handleSubmit : handleNext}
              sx={{ flex: 1 }}
            >
              {hasClients ? "Login" : "Next"}
            </LoadingButton>
          </Box>

          {hasClients && (
            <Button
              type="button"
              variant="text"
              onClick={() => navigate("/forgot-password")}
              sx={(theme) => ({
                alignSelf: "center",
                minHeight: 0,
                ...theme.typography["body01-link"],
                color: theme.palette.primary["clr-300"],
                "&:hover": {
                  backgroundColor: "transparent",
                  textDecoration: "underline",
                },
              })}
            >
              forgot password?
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
}

export default MobileLogin;
